import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import Content from '../../../layout/Content';
import { CABIN_CREW_MOVEMENT_HISTORY_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import {
  clearCabinCrewMovementHistory,
  getCabinCrewMovementHistory,
  putCabinCrewMovementHistory,
} from '../../../../actions';
import NoDataResource from '../../../common/resource/NoDataResource';
import CabinCrewMovementHistoryForm from './CabinCrewMovementHistoryForm';
import Loader from '../../../common/Loader';
import { tzNormalizeDate } from '../../../../utils/date';
import { MONTH_OPTIONS } from '../../../../config/constants';

const EditCabinCrewMovementHistory = ({
  breadcrumbs,
  match: {
    params: { id },
  },
  loading,
  cabinCrewMovementHistory,
  dispatchGetCabinCrewMovementHistory,
  dispatchClearCabinCrewMovementHistory,
  dispatchPutCabinCrewMovementHistory,
}) => {
  useLayoutEffect(() => {
    dispatchGetCabinCrewMovementHistory({ cabinCrewMovementHistoryId: id });
    return () => dispatchClearCabinCrewMovementHistory();
  }, []);

  const onSubmit = (formValues) => {
    const { month, year, driverMovement } = formValues;

    const startDate = tzNormalizeDate({
      date: formValues.startDate,
    });

    const endDate = tzNormalizeDate({
      date: formValues.endDate,
    });

    if (moment(startDate).isAfter(moment(endDate))) {
      toastr.error(
        'Error',
        'La fecha de inicio no puede ser mayor a la fecha de fin',
      );
      return;
    }

    const newFormValues = {
      ...formValues,
      startDate,
      endDate,
    };

    const isVacation = !!driverMovement.vacation || !!driverMovement.paidVacation;

    if (driverMovement.maxDaysPerMonth) {
      newFormValues.chargeMonth = month.value;
      newFormValues.chargeYear = Number(year);
    } else if (isVacation) {
      newFormValues.chargeMonth = null;
      newFormValues.chargeYear = formValues.vacationPeriod ? formValues.vacationPeriod.value : null;
    } else {
      newFormValues.chargeMonth = null;
      newFormValues.chargeYear = null;
    }

    dispatchPutCabinCrewMovementHistory(id, newFormValues);
  };

  const generateInitialValues = ({
    note,
    secondaryNote,
    cabinCrewMovementName,
    cabinCrewFullName,
    cabinCrewMovementId,
    cabinCrewMovementMaxDaysPerMonth,
    driverMovementVacation,
    driverMovementPaidVacation,
    chargeMonth,
    cabinCrewId,
    chargeYear
  }) => ({
    note,
    secondaryNote,
    cabinCrew: {
      value: cabinCrewId,
      label: cabinCrewFullName,
    },
    driverMovement: {
      value: cabinCrewMovementId,
      label: cabinCrewMovementName,
      maxDaysPerMonth: cabinCrewMovementMaxDaysPerMonth,
      vacation: driverMovementVacation,
      paidVacation: driverMovementPaidVacation,
    },
    startDate: tzNormalizeDate({ date: cabinCrewMovementHistory.get('startDate') }).slice(0, 16),
    endDate: tzNormalizeDate({ date: cabinCrewMovementHistory.get('endDate') }).slice(0, 16),
    year: chargeYear,
    cabinCrewMovementMaxDaysPerMonth,
    driverMovementVacation,
    driverMovementPaidVacation,
    month: chargeMonth ?
      MONTH_OPTIONS.find((m) => m.value === chargeMonth) : null,
  });

  let content;

  if (loading) content = <Loader />;
  else if (cabinCrewMovementHistory.isEmpty())
    content = <NoDataResource returnPage={CABIN_CREW_MOVEMENT_HISTORY_PATH} />;
  else {
    content = (
      <CabinCrewMovementHistoryForm
        onSubmit={onSubmit}
        isEdit
        initialValues={generateInitialValues(cabinCrewMovementHistory.toJS())}
      />
    );
  }
  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Movimiento del Tripulante"
      content={content}
    />
  );
};

EditCabinCrewMovementHistory.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  match: matchPropTypes.isRequired,
  dispatchGetCabinCrewMovementHistory: PropTypes.func.isRequired,
  dispatchClearCabinCrewMovementHistory: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  cabinCrewMovementHistory: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchPutCabinCrewMovementHistory: PropTypes.func.isRequired,
};

const mapStateToProps = (
  { TrafficUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...TrafficUnit.CabinCrewUnitHome.get('breadcrumbs'),
    {
      text: 'Historial de movimiento del tripulante',
      href: CABIN_CREW_MOVEMENT_HISTORY_PATH,
    },
    {
      text: 'Ver',
      href: `${CABIN_CREW_MOVEMENT_HISTORY_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  loading: !TrafficUnit.CabinCrewMovementHistory.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  cabinCrewMovementHistory: TrafficUnit.CabinCrewMovementHistory.getIn([
    'current',
    'content',
  ]),
});

const mapDispatchToProps = {
  dispatchGetCabinCrewMovementHistory: getCabinCrewMovementHistory,
  dispatchClearCabinCrewMovementHistory: clearCabinCrewMovementHistory,
  dispatchPutCabinCrewMovementHistory: putCabinCrewMovementHistory,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditCabinCrewMovementHistory);